import * as Api from '@ViewModels';
import { css } from 'aphrodite';
import { observer } from 'mobx-react';
import * as React from 'react';
import { ILocationState } from '../../../models';
import { BlogPostRequest } from '../../../models/Blogs';
import { useErrorMessages, useFullscreenModal, useUserSession } from '../../../models/hooks/appStateHooks';
import {
	invalidateBlogPostDrafts,
	useBlogCardsAllCategoryTemplates,
	useBlogCardsCategories,
	useBlogCardsCategoryTemplates,
	useBlogPostDraftsInfiniteQuery,
	useBlogsCardSearchTemplates,
	useDeleteBlogPostMutation,
	useTemplateExportMutation,
} from '../../../queries';
import { IBlogTemplateCard, KnownCategories } from '../../../viewmodels/AppViewModels';
import { Button } from '../../components-v2/Button';
import { Dropdown, DropdownContent, DropdownEllipsisTrigger, DropdownItem } from '../../components-v2/Dropdown';
import { ConfirmationDialog, DefaultDeleteConfirmationOptions } from '../../components/ConfirmationDialog';
import { ExportConfirmationModalV2 } from '../../components/ExportConfirmation';
import { LoadingSpinner } from '../../components/LoadingSpinner';
import { BlogEmptyGraphic } from '../../components/svgs/graphics/BlogEmptyGraphic';
import { ArrowIcon } from '../../components/svgs/icons/ArrowIcon';
import { WarningIcon } from '../../components/svgs/icons/WarningIcon';
import { BlogPostTemplateCard } from '../../components/templateCards/BlogPostTemplateCard';
import { baseStyleSheet, bs } from '../../styles/styles';
import { CampaignBrowser, useCampaignParams, useEnforceIndustryAndCategory } from '../CampaignBrowser';
import {
	ContentLibraryContent,
	ContentLibrarySection,
	ContentLibrarySectionHeader,
	ContentLibrarySectionHeaderTitle,
} from './presentation';
import { styleSheet } from './styles';

export const BlogsCampaignCards = observer(() => {
	const fullscreenModal = useFullscreenModal();
	const userSession = useUserSession();
	const errorMessages = useErrorMessages();
	const { category, industry, search, setCampaignBrowserParam } = useCampaignParams();
	const isAllCategory = category === KnownCategories.All;
	const [showConfirmationModal, setShowConfirmationModal] = React.useState(false);
	const [blogToDelete, setBlogToDelete] = React.useState<Api.IBlogPost | null>(null);
	const isMyBlogDaftsCategory = category === KnownCategories.BlogPostDrafts;

	const templateExportMutation = useTemplateExportMutation({
		onError: error => {
			errorMessages.pushApiError(error);
			templateExportMutation.reset();
		},
	});

	const templateExportEnabled = userSession?.account?.features?.crossAccountTemplateSharing?.enabled;

	useEnforceIndustryAndCategory({ setCampaignBrowserParam, tabUrlParam: 'blogs' });

	const isSearching = Boolean(search);

	const searchTemplatesQuery = useBlogsCardSearchTemplates({
		enabled: isSearching,
		industry,
		search,
	});

	const myDraftsQuery = useBlogPostDraftsInfiniteQuery({
		enabled: category === KnownCategories.BlogPostDrafts,
		userId: userSession.user!.id,
	});

	const deleteBlogPostMutation = useDeleteBlogPostMutation({
		onSuccess: () => {
			setBlogToDelete(null);
			invalidateBlogPostDrafts({ userId: userSession.user.id });
		},
		onError: error => {
			errorMessages.pushApiError(error);
		},
	});

	const handleTemplateClick = (templateCard: IBlogTemplateCard) => {
		const locationState: ILocationState<any, BlogPostRequest> = {
			model: {
				templateCard,
			},
		};
		fullscreenModal.history.push({
			pathname: `/blog/post/create-from-template/${templateCard.id}`,
			state: locationState,
		});
	};

	const categoryQuery = useBlogCardsCategories({
		enabled: Boolean(industry),
		industry,
		withAll: true,
		withMyTemplates: false,
		withDrafts: userSession.account?.features?.blogFeature.enabled,
	});

	const categoryTemplatesQuery = useBlogCardsCategoryTemplates({
		category,
		enabled: !isAllCategory && !isMyBlogDaftsCategory,
		industry,
	});

	const allCategoryTemplatesQuery = useBlogCardsAllCategoryTemplates({
		enabled: isAllCategory,
		industry,
	});

	const hasData = React.useMemo(() => {
		return (
			(isSearching && searchTemplatesQuery.data && searchTemplatesQuery.data.length > 0) ||
			(!isSearching &&
				((isAllCategory &&
					allCategoryTemplatesQuery.data &&
					Object.values(allCategoryTemplatesQuery.data).flat().length > 0) ||
					(!isAllCategory &&
						!isMyBlogDaftsCategory &&
						categoryTemplatesQuery.data &&
						categoryTemplatesQuery.data.length > 0) ||
					(isMyBlogDaftsCategory && myDraftsQuery.data && myDraftsQuery.data.pages.length > 0)))
		);
	}, [
		isSearching,
		searchTemplatesQuery.data,
		isAllCategory,
		allCategoryTemplatesQuery.data,
		isMyBlogDaftsCategory,
		categoryTemplatesQuery.data,
		myDraftsQuery.data,
	]);

	const isLoading =
		(isSearching && searchTemplatesQuery.isPending) ||
		(!isSearching &&
			((isAllCategory && allCategoryTemplatesQuery.isPending) ||
				(!isAllCategory && !isMyBlogDaftsCategory && categoryTemplatesQuery.isPending) ||
				(isMyBlogDaftsCategory && myDraftsQuery.isPending)));

	const handleChangeIndustry = (value: Api.Industry) => {
		setCampaignBrowserParam({ industry: value, category: KnownCategories.All });
	};
	const handleChangeCategory = (value: string) => {
		setCampaignBrowserParam({ category: value });
	};
	const handleSearchChange = React.useCallback(
		(value: string) => {
			setCampaignBrowserParam({ search: value });
		},
		[setCampaignBrowserParam]
	);
	const onBlogPostClicked = React.useCallback(
		async (blog: Api.IBlogPost) => {
			if (blog.id) {
				const locationState: ILocationState<any, BlogPostRequest> = {
					model: {
						post: blog,
					},
				};
				fullscreenModal?.history?.push({
					pathname: `/blog/post/edit/${blog.id}`,
					state: locationState,
				});
			}
		},
		[fullscreenModal?.history]
	);

	const onCreateNewBlogPostClicked = () => {
		fullscreenModal.history.push({
			pathname: '/blog/post/create',
		});
	};

	const onExportTemplates = () => {
		if (templateExportMutation.isIdle) {
			templateExportMutation.mutate({});
		}
	};

	const onDeleteConfirmationRequestClose = (_: any, cancel: boolean) => {
		if (cancel) {
			setShowConfirmationModal(false);
			return;
		}

		deleteBlogPostMutation.mutate({ id: blogToDelete?.id });
		setShowConfirmationModal(false);
	};

	return (
		<>
			<CampaignBrowser
				categories={categoryQuery.data ?? []}
				category={category}
				industry={industry}
				onCategoryChange={handleChangeCategory}
				onIndustryChange={handleChangeIndustry}
				onSearchChange={handleSearchChange}
				renderRightActions={
					<div style={{ display: 'flex', gap: '8px' }}>
						<button className={css(baseStyleSheet.ctaButton)} onClick={onCreateNewBlogPostClicked}>
							<span>Create New Blog Post</span>
						</button>
						{templateExportEnabled && (
							<button className={css(baseStyleSheet.ctaButton)} onClick={onExportTemplates}>
								<span>Export Templates</span>
							</button>
						)}
					</div>
				}
				search={search}
				searchPlaceholder='Search blog templates or keywords'
			>
				<ContentLibraryContent>
					{isSearching && searchTemplatesQuery.data && searchTemplatesQuery.data?.length ? (
						<ContentLibrarySection>
							<div className={css(bs.gap5, bs.grid, bs.p6, bs.boxBorder, bs.overflowAuto, styleSheet.cardList)}>
								{searchTemplatesQuery.data.map(template => (
									<BlogPostTemplateCard
										key={template.id}
										title={template.name}
										description={template.summary}
										onTemplateCardClick={() => handleTemplateClick(template)}
									/>
								))}
							</div>
						</ContentLibrarySection>
					) : null}
					{!isSearching && !isAllCategory && !isMyBlogDaftsCategory && categoryTemplatesQuery.data?.length ? (
						<ContentLibrarySection>
							<ContentLibrarySectionHeader>
								<ContentLibrarySectionHeaderTitle>{category}</ContentLibrarySectionHeaderTitle>
							</ContentLibrarySectionHeader>
							<div className={css(bs.gap5, bs.grid, bs.p6, bs.boxBorder, bs.overflowAuto, styleSheet.cardList)}>
								{categoryTemplatesQuery.data.map(template => (
									<BlogPostTemplateCard
										key={template.id}
										title={template.name}
										description={template.summary}
										onTemplateCardClick={() => handleTemplateClick(template)}
									/>
								))}
							</div>
						</ContentLibrarySection>
					) : null}

					{!isSearching && isMyBlogDaftsCategory && myDraftsQuery.data ? (
						<ContentLibrarySection>
							<ContentLibrarySectionHeader>
								<ContentLibrarySectionHeaderTitle>{category}</ContentLibrarySectionHeaderTitle>
							</ContentLibrarySectionHeader>
							<div className={css(bs.gap5, bs.grid, bs.p6, bs.boxBorder, bs.overflowAuto, styleSheet.cardList)}>
								{myDraftsQuery.data.pages.map(group =>
									group.values.map(blog => {
										if (group.totalCount === 0) {
											return null;
										}

										return (
											<BlogPostTemplateCard
												key={blog.id}
												title={blog.title}
												description={blog.content.document.replace(/<[^>]*>?/gm, '')}
												onTemplateCardClick={() => onBlogPostClicked(blog)}
												cardContextMenu={
													<Dropdown>
														<div className={css(styleSheet.moreMenu)}>
															<DropdownEllipsisTrigger />
														</div>
														<DropdownContent>
															<DropdownItem onClick={() => onBlogPostClicked(blog)}>Edit</DropdownItem>
															<DropdownItem
																onClick={() => {
																	setShowConfirmationModal(true);
																	setBlogToDelete(blog);
																}}
															>
																Delete
															</DropdownItem>
														</DropdownContent>
													</Dropdown>
												}
											/>
										);
									})
								)}
							</div>
						</ContentLibrarySection>
					) : null}
					{!isSearching &&
					isAllCategory &&
					allCategoryTemplatesQuery.data &&
					Object.keys(allCategoryTemplatesQuery.data).filter(x => x !== KnownCategories.Featured).length
						? Object.keys(allCategoryTemplatesQuery.data).map((categoryName: string) => {
								const templates = allCategoryTemplatesQuery.data[categoryName] ?? [];
								if (!templates?.length) {
									return null;
								}
								return (
									<ContentLibrarySection key={categoryName}>
										<ContentLibrarySectionHeader>
											<ContentLibrarySectionHeaderTitle>
												{categoryName === KnownCategories.Me ? KnownCategories.MyTemplates : categoryName}
											</ContentLibrarySectionHeaderTitle>
											<Button variant='link' size='sm' onClick={() => handleChangeCategory(categoryName)}>
												Browse {categoryName === KnownCategories.Me ? KnownCategories.MyTemplates : categoryName}{' '}
												<ArrowIcon fillColor='currentColor' styles={[bs.mt1]} />
											</Button>
										</ContentLibrarySectionHeader>
										<div className={css(bs.gap5, bs.grid, bs.p6, bs.boxBorder, bs.overflowAuto, styleSheet.cardList)}>
											{templates.map(template => (
												<BlogPostTemplateCard
													key={template.id}
													title={template.name}
													description={template.summary}
													onTemplateCardClick={() => handleTemplateClick(template)}
												/>
											))}
										</div>
									</ContentLibrarySection>
								);
							})
						: null}
					{isLoading && <LoadingSpinner className={css(styleSheet.pageLoading)} />}
					{!isLoading && !hasData ? (
						<div className={css(bs.gap5, bs.grid, bs.p6, bs.boxBorder, bs.overflowAuto, styleSheet.cardListEmpty)}>
							<figure>
								<BlogEmptyGraphic />
							</figure>
							<p>We did not find any blog templates</p>
						</div>
					) : null}

					<ConfirmationDialog
						icon={<WarningIcon />}
						modalProps={{
							isOpen: showConfirmationModal,
							onRequestClose: onDeleteConfirmationRequestClose,
						}}
						options={DefaultDeleteConfirmationOptions}
						title='Are you sure you want to delete this blog post?'
					/>
				</ContentLibraryContent>
			</CampaignBrowser>
			{!templateExportMutation.isIdle && (
				<ExportConfirmationModalV2
					mutationResult={templateExportMutation}
					modalProps={{
						isOpen: !templateExportMutation.isIdle,
						onRequestClose: () => templateExportMutation.reset(),
					}}
					title='Export Templates to CSV'
					documentKind='template export'
					subTitle={
						<>
							<div>The export includes template name, type, keywords,</div>
							<div>creation date, and template ID for all your templates.</div>
						</>
					}
				/>
			)}
		</>
	);
});
